import React from "react";
import { SvgIcon } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import caseStates from "@_configs/caseStates.config";
import CancellationReasons, {
  CANCELLATION_REASON_OTHER,
} from "@_configs/cancellationReasons.config";
import { getCurrencySign, toAmountNotation } from "@_utils/helpers";
import { useActivityExpand } from "../hooks/useActivityExpand";
import { parseTextLines, noteLinesNeedExpand } from "../utils/parseTextLines";
import CardShell from "../core/CardShell";
import styles from "../CaseActivityCard.module.css";

// ── Icons ─────────────────────────────────────────────────────────────────────

const StateChangeIcon = () => (
  <SvgIcon>
    <path d="M3 11c0 2.45 1.76 4.47 4.08 4.91l-1.49-1.49L7 13l4 4.01L7 21l-1.41-1.41 1.58-1.58v-.06C3.7 17.54 1 14.58 1 11c0-3.87 3.13-7 7-7h3v2H8c-2.76 0-5 2.24-5 5zm19 0V4h-9v7h9zm-2-2h-5V6h5v3zm-7 4h9v7h-9z" />
  </SvgIcon>
);

const CaseCreatedIcon = () => (
  <SvgIcon>
    <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11zM8 15.01l1.41 1.41L11 14.84V19h2v-4.16l1.59 1.59L16 15.01 12.01 11z" />
  </SvgIcon>
);

// ── Helpers ───────────────────────────────────────────────────────────────────

// Max characters a single-line "Other" note can have before it needs to be
// truncated (and therefore made expandable) on the 12px / 400px secondary line.
const NOTE_PREVIEW_MAX_CHARS = 80;

const toStateName = (state) => caseStates[state]?.name ?? state;

const getCancellationReasonLabel = (reason) =>
  CancellationReasons.find((item) => item.id === reason)?.label ?? reason;

// Placeholder the backend stores on older cancelled activities that predate
// reason collection. It is not a real reason, so it is hidden.
const UNKNOWN_REASON = "UNKNOWN";

// Renders note lines the same way NoteActivityCard does (one block per line),
// but at the 12px secondary-line size.
const renderNoteLines = (lines) =>
  lines.map((line, index) => (
    <div className={`${styles.note_line} ${styles.note_line_sm}`} key={index}>
      {line}
    </div>
  ));

// ── Components ────────────────────────────────────────────────────────────────

/**
 * StateChangeActivityCard
 *
 * Renders CASEACTIVITY_TYPE_STATE_CHANGE rows.
 * Shows "From <old state> to <new state>". For cancellations, a secondary line
 * shows the reason (and paid amount when present). A note makes the card
 * expandable: for "Other" the note is the preview; otherwise it appears under
 * "Additional details:".
 */
export function StateChangeActivityCard({ activity }) {
  const { detailsOpen, animateExpand, toggle } = useActivityExpand();

  const {
    cancellation_reason: cancellationReason,
    note,
    paid_amount: paidAmount,
    currency,
  } = activity.content.additional_info ?? {};

  const hasNote = Boolean(note?.trim());
  const isOther = cancellationReason === CANCELLATION_REASON_OTHER;
  const showNotePreview = isOther && hasNote;
  const showCancellationReason =
    Boolean(cancellationReason) &&
    cancellationReason.toUpperCase() !== UNKNOWN_REASON;
  const noteLines = hasNote ? parseTextLines(note) : [];

  // "Other" notes are shown inline, so they only expand when they overflow.
  // Other reasons hide the note entirely, so any note makes the card expand.
  const canExpand = showNotePreview
    ? noteLinesNeedExpand(noteLines, NOTE_PREVIEW_MAX_CHARS)
    : hasNote;

  const paidAmountText =
    paidAmount != null
      ? ` (${getCurrencySign(currency)} ${toAmountNotation(paidAmount)})`
      : "";

  const openerColumn = canExpand ? (
    <div
      className={styles.opener_expand_wrap}
      data-testid="state-change-expand-toggle"
      onClick={(e) => {
        e.stopPropagation();
        toggle();
      }}
    >
      <ExpandMore
        className={`${styles.opener_expand_more} ${
          detailsOpen ? styles.gray : ""
        }`}
      />
    </div>
  ) : null;

  return (
    <CardShell
      activity={activity}
      animateExpand={animateExpand}
      icon={<StateChangeIcon />}
      typeText="State changed"
      typeSubText={activity.user_name}
      onContentClick={canExpand ? toggle : undefined}
      hasOpener={canExpand}
      openerColumn={openerColumn}
      contentPreview={
        <div>
          <div>
            From {toStateName(activity.content.old_state)} to{" "}
            <b>{toStateName(activity.content.new_state)}</b>
          </div>

          {/* "Other" reason: the note itself is the preview. */}
          {showNotePreview &&
            (canExpand && !detailsOpen ? (
              <div
                className={`${styles.note_line_truncated} ${styles.note_line_sm}`}
              >
                {noteLines.join(" ")}
              </div>
            ) : (
              renderNoteLines(noteLines)
            ))}

          {/* Other reasons: reason label (+ paid amount). */}
          {!showNotePreview && showCancellationReason && (
            <div className={styles.content_secondary_line}>
              {getCancellationReasonLabel(cancellationReason)}
              {paidAmountText}
            </div>
          )}

          {/* Other reasons: note revealed inline after "Additional details:". */}
          {!showNotePreview && hasNote && detailsOpen && (
            <div className={styles.content_secondary_line}>
              <span>Additional details:</span>{" "}
              <span className={styles.note_preline}>
                {noteLines.join("\n")}
              </span>
            </div>
          )}
        </div>
      }
    />
  );
}

/**
 * CaseCreatedActivityCard
 *
 * Renders CASEACTIVITY_TYPE_CASE_CREATED rows.
 * No expandable content — just a timestamp marker.
 */
export function CaseCreatedActivityCard({ activity }) {
  return (
    <CardShell
      activity={activity}
      icon={<CaseCreatedIcon />}
      typeText="Case created"
      typeSubText={activity.user_name}
    />
  );
}
